// frontend/src/api/apiService.js - VERSION SÉPARATION CLIENT / ADMIN
import axios from "axios";
import i18n from "../i18n/i18n";
import { mapErrorToCode, resolveError } from "../i18n/errorCatalog";
import { reportFrontendError } from "../utils/errorReporter";

// Statuts pour lesquels un message backend brut ne doit JAMAIS être montré à l'utilisateur
// (auth, rate-limit, timeout, 5xx) → on force le catalogue traduit.
// NB : 403 volontairement absent — un 403 métier (isolation tenant…) porte souvent un
// message clair à conserver ; sans message, attachErrorContext retombe sur le catalogue.
const CATALOG_ONLY_STATUSES = new Set([401, 408, 429, 500, 502, 503, 504]);

// Attache appCode + requestId + userMessage (traduit via le catalogue). Le TEXTE ne vient
// plus jamais du backend pour les erreurs techniques ; seuls les 4xx métier avec message FR
// intentionnel et sans code mappé conservent transitoirement leur message (migration E4/E5).
const attachErrorContext = (error) => {
  const appCode = mapErrorToCode(error);
  error.appCode = appCode;
  const reqId = error.response?.headers?.["x-request-id"] || error.response?.data?.requestId;
  if (reqId) error.requestId = reqId;

  const tr = (k, d) => i18n.t(k, { ns: "common", defaultValue: d });
  const status = error.response?.status;
  const backendMsg = error.response?.data?.message;
  const hasMappedCode = Boolean(error.response?.data?.code) && appCode !== "GENERIC";
  const forceCatalog = !error.response || hasMappedCode || CATALOG_ONLY_STATUSES.has(status) || status >= 500;

  const resolved = resolveError(appCode, tr);
  error.userMessage = forceCatalog ? resolved.description : (backendMsg || resolved.description);
  // 🔖 Erreur serveur : on ajoute la référence (requestId) pour que l'utilisateur puisse la
  // communiquer au support ; le détail technique reste, lui, uniquement dans les logs serveur.
  if (error.requestId && (appCode === 'SERVER_ERROR' || appCode === 'SERVER_UNAVAILABLE')) {
    error.userMessage += ' — ' + i18n.t('errors.reference', { ns: 'common', id: error.requestId, defaultValue: `Référence : ${error.requestId}` });
  }
  // Contenu structuré (titre/description/actions/gravité) pour les UI qui savent l'exploiter.
  error.userError = { ...resolved, requestId: error.requestId };

  // 🔗 Pont E4 : pour une erreur TECHNIQUE (catalogue forcé), on remplace aussi le
  // data.message brut par le message traduit. Ainsi les nombreux composants qui lisent
  // encore `err.response.data.message` directement affichent le message sûr, sans édition.
  if (forceCatalog && error.response?.data && typeof error.response.data === 'object') {
    error.response.data.message = error.userMessage;
  }
  return error.userMessage;
};

const isDev = process.env.NODE_ENV === 'development';
const log = isDev ? (...a) => console.log(...a) : () => {};
const logWarn = isDev ? (...a) => console.warn(...a) : () => {};

const PROD_API = 'https://api.gowele.com/api';

const resolveBaseURL = () => {
  if (isDev) return "http://localhost:5000/api";
  // Always use the env var when provided (Vercel / Render static), otherwise the custom domain
  return process.env.REACT_APP_API_URL || PROD_API;
};

const API_CONFIG = {
  baseURL: resolveBaseURL(),
  timeout: 30000, // 30s : couvre les requêtes lentes pendant le réveil du serveur Render (cold-start)
  retryAttempts: 3,
  retryDelay: 1000
};

// Cache intelligent
class SmartCache {
  constructor() {
    this.cache = new Map();
  }

  set(key, data, timeoutMs = 30000) {
    this.cache.set(key, {
      data,
      expiresAt: Date.now() + timeoutMs,
    });
  }

  get(key) {
    const entry = this.cache.get(key);
    if (!entry) return null;
    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return null;
    }
    return entry.data;
  }

  clearByPattern(pattern) {
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) this.cache.delete(key);
    }
  }

  clear() { 
    this.cache.clear(); 
  }

  getSize() {
    return this.cache.size;
  }
}

const smartCache = new SmartCache();

// Token CSRF stocké en mémoire : le JS cross-origin (gowele.com → api.gowele.com)
// ne peut pas lire les cookies du backend via document.cookie.
// L'intercepteur de réponse capture le token depuis le header X-CSRF-Token
// et le stocke ici pour l'envoyer sur toutes les requêtes mutantes.
let csrfTokenMemory = null;

// 🔄 Rafraîchissement de session (refresh token) — dédoublonne les appels concurrents :
// plusieurs requêtes qui échouent en 401 partagent UN seul POST /auth/refresh.
let refreshPromise = null;

// 🔒 SÉCURITÉ
const getCSRFToken = () => {
  // Priorité : mémoire (cross-origin) > cookie (same-origin / dev) > meta tag
  if (csrfTokenMemory) return csrfTokenMemory;
  const cookieValue = document.cookie
    .split('; ')
    .find(row => row.startsWith('XSRF-TOKEN='))
    ?.split('=')[1];
  const metaTag = document.querySelector('meta[name="csrf-token"]');
  return cookieValue || metaTag?.getAttribute('content') || null;
};

const sanitizeForLogging = (data) => {
  if (!data || typeof data !== 'object') return data;
  
  const sensitiveFields = [
    'password', 'token', 'cvv', 'cardNumber', 
    'carteBancaire', 'crypto', 'authorization',
    'iban', 'secret', 'privateKey', 'codeSecurite',
    'mot_de_passe', 'code_admin', 'newPassword',
    'currentPassword', 'confirmPassword'
  ];
  
  const sanitized = { ...data };
  
  sensitiveFields.forEach(field => {
    if (sanitized[field]) {
      sanitized[field] = '***';
    }
  });
  
  if (sanitized.headers?.Authorization) {
    sanitized.headers.Authorization = 'Bearer ***';
  }
  
  return sanitized;
};

const isValidEndpoint = (url) => {
  if (!url || typeof url !== 'string') return false;
  
  const lowerUrl = url.toLowerCase();
  
  const suspiciousPatterns = [
    { test: () => lowerUrl.startsWith('javascript:'), reason: 'javascript protocol' }, // eslint-disable-line no-script-url
    { test: () => lowerUrl.startsWith('data:'), reason: 'data protocol' },
    { test: () => lowerUrl.includes('..'), reason: 'parent directory traversal' },
    { test: () => lowerUrl.includes('//') && !lowerUrl.includes('://'), reason: 'double slash' },
  ];
  
  return !suspiciousPatterns.some(pattern => pattern.test());
};

// Instance axios
const api = axios.create({
  baseURL: API_CONFIG.baseURL,
  headers: { "Content-Type": "application/json" },
  timeout: API_CONFIG.timeout,
  // 🍪 withCredentials : envoie automatiquement le cookie httpOnly authToken
  // avec chaque requête cross-origin (Render frontend → Render backend)
  withCredentials: true,
});

// 🔐 INTERCEPTEUR REQUÊTE
api.interceptors.request.use((config) => {
  if (!isValidEndpoint(config.url)) {
    console.error('❌ URL suspecte bloquée:', config.url);
    throw new Error('URL non valide');
  }

  // 👉 DÉTECTION CONTEXTE ADMIN vs CLIENT
  const isAdminApi = config.url?.startsWith('/admin/');
  let token = null;

  if (isAdminApi) {
    // Admin : toujours via localStorage (les admins n'utilisent pas les cookies client)
    token = localStorage.getItem("adminToken") || sessionStorage.getItem("adminToken");
    // 🔒 M1/V2-M1 : REACT_APP_ADMIN_SECRET RETIRÉ. CRA inline toute variable REACT_APP_* en
    // clair dans le bundle → ce « secret » était public (visible dans les DevTools), donc un
    // FAUX facteur de sécurité. La vraie autorisation admin repose sur le JWT signé par
    // ADMIN_JWT_SECRET (frontière S2), pas sur ce header. On ne l'envoie plus.
    // 🪪 « Voir en tant que compagnie » (super-admin) : scope les écrans de l'espace
    // compagnie sur la compagnie sélectionnée. Ignoré côté backend pour les non-super-admins.
    // Vide => accès global normal. On N'applique PAS le scope aux écrans de gestion
    // PLATEFORME du super-admin (liste des compagnies, super-panel, utilisateurs, fintech,
    // visiteurs, frais de service, paramètres) : ils doivent rester globaux — c'est aussi
    // la source du sélecteur de compagnie (il doit voir TOUTES les compagnies).
    const viewCompanyId = localStorage.getItem("goweleViewCompanyId");
    if (viewCompanyId && viewCompanyId !== "null" && viewCompanyId !== "undefined") {
      const u = config.url || '';
      const GLOBAL_ONLY = ['/admin/societes', '/admin/super', '/admin/users', '/admin/utilisateurs',
        '/admin/fintech', '/admin/visitors', '/admin/service-fees', '/admin/frais-service',
        '/admin/settings', '/admin/system'];
      // 🔒 JAMAIS sur l'auth/health : ces routes ne se scopent pas, et envoyer un header
      // custom sur le LOGIN déclenche un préflight CORS qui, si le serveur ne l'autorise pas,
      // bloque la requête (« Network Error ») → login impossible. Défense en profondeur.
      const NEVER = ['/admin/auth', '/admin/health'];
      const isGlobalOnly = GLOBAL_ONLY.some(p => u.startsWith(p));
      const isNever = NEVER.some(p => u.startsWith(p));
      if (!isGlobalOnly && !isNever) config.headers['x-view-company-id'] = viewCompanyId;
    }
  } else {
    // Client : les nouvelles sessions utilisent le cookie httpOnly (withCredentials:true ci-dessus).
    // Le token localStorage sert UNIQUEMENT de fallback pour les sessions ouvertes AVANT
    // la migration - il sera absent pour toutes les nouvelles connexions.
    token = localStorage.getItem("authToken") || sessionStorage.getItem("authToken") || null;
  }

  // N'ajoute le header Bearer que si un token localStorage existe (fallback sessions legacy)
  // Les nouvelles sessions n'ont pas de token en localStorage → le cookie httpOnly suffit
  if (token && token !== "undefined" && token !== "null") {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Langue courante → backend peut localiser ses messages d'erreur
  const lang = localStorage.getItem('gowele_lang') || 'fr';
  config.headers['Accept-Language'] = lang;

  if (['post', 'put', 'delete', 'patch'].includes(config.method?.toLowerCase())) {
    const csrfToken = getCSRFToken();
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
  }

  log(`🚀 API ${config.method?.toUpperCase()} ${config.url}`, {
    data: sanitizeForLogging(config.data),
    params: config.params,
    timestamp: new Date().toISOString(),
    hasToken: !!token,
    context: isAdminApi ? 'admin' : 'client'
  });

  return config;
}, (error) => {
  console.error('❌ Erreur intercepteur requête:', error);
  return Promise.reject(error);
});

// 🔐 INTERCEPTEUR RÉPONSE
api.interceptors.response.use(
  (response) => {
    // Capture du token CSRF exposé par le backend dans X-CSRF-Token.
    // Nécessaire en cross-origin : document.cookie ne peut pas lire
    // les cookies de api.gowele.com depuis gowele.com.
    const newToken = response.headers['x-csrf-token'];
    if (newToken) csrfTokenMemory = newToken;

    log(`✅ API ${response.status} ${response.config.url}`, {
      timestamp: new Date().toISOString(),
      success: response.data?.success
    });
    return response;
  },
  async (error) => {
    // 🔑 CSRF : capturer le token AUSSI sur les réponses d'ERREUR (401/403/…). Le backend pose
    // X-CSRF-Token sur CHAQUE réponse ; si on ne le captait que sur les succès, un parcours qui
    // commence par des erreurs (ex. /auth/verify 401 au boot admin) laissait csrfTokenMemory=null
    // → TOUTES les mutations admin repartaient sans token → 403 CSRF_TOKEN_INVALID systématique.
    const errTokenHeader = error.response?.headers?.['x-csrf-token'];
    if (errTokenHeader) csrfTokenMemory = errTokenHeader;

    const errorDetails = {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.message,
      timestamp: new Date().toISOString()
    };

    if (isDev) console.error("❌ API Error:", errorDetails);

    // 🚧 MAINTENANCE : un 503 avec { maintenance:true } n'est PAS une erreur — c'est un état attendu.
    // Sans ce court-circuit, chaque appel de fond pendant la maintenance déclenchait un TOAST d'erreur
    // (503 ∈ statuts catalogue) ET un signalement d'incident → messages d'erreur qui s'empilent en
    // arrière-plan derrière l'écran de maintenance + bruit d'incidents. On rejette silencieusement :
    // l'écran de maintenance (piloté par /api/status) prend le relais côté affichage.
    if (error.response?.status === 503 && error.response?.data?.maintenance === true) {
      error.isMaintenance = true;
      return Promise.reject(error); // ni toast, ni incident, ni message catalogue
    }

    // 🔁 CSRF auto-réparation : un 403 CSRF_TOKEN_INVALID signifie qu'on est parti sans token
    // (ou avec un token périmé). On vient de capturer le token FRAIS de cette réponse d'erreur
    // (ci-dessus) → on REJOUE la requête UNE fois avec ce token. Évite que la 1re mutation admin
    // échoue définitivement quand csrfTokenMemory n'était pas encore peuplé.
    if (error.response?.status === 403 && error.response?.data?.code === 'CSRF_TOKEN_INVALID') {
      const cfg = error.config || {};
      if (!cfg._csrfRetried && errTokenHeader) {
        cfg._csrfRetried = true;
        cfg.headers = { ...(cfg.headers || {}), 'X-CSRF-Token': errTokenHeader };
        return api(cfg);
      }
    }

    // 🪪 Lot 2 — contexte « Voir en tant que compagnie » invalide (inexistante 404 / indisponible 409).
    // On NE replie PAS silencieusement vers une autre compagnie : on retire le contexte, on purge le
    // cache, on repasse en VUE GLOBALE et on affiche un message clair. L'utilisateur reste maître.
    const viewCode = error.response?.data?.code;
    if (viewCode === 'VIEW_COMPANY_NOT_FOUND' || viewCode === 'VIEW_COMPANY_UNAVAILABLE' || viewCode === 'VIEW_COMPANY_CHECK_FAILED') {
      try {
        localStorage.removeItem('goweleViewCompanyId');
        smartCache.clear();
        window.dispatchEvent(new CustomEvent('gowele:view-company-changed', { detail: { companyId: null } }));
        window.dispatchEvent(new CustomEvent('admin:data_changed', { detail: {} }));
        window.__toast?.({ type: 'warning', message: error.response?.data?.message || 'Compagnie indisponible — retour à la vue globale.', duration: 8000 });
      } catch (_) {}
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const isAdminApi = url.startsWith('/admin/');

      const publicClientUrls = ['/auth/login', '/auth/signup', '/auth/verify-otp', '/auth/resend-otp', '/auth/verify-email', '/auth/forgot-password', '/auth/reset-password', '/auth/verify', '/health'];
      // ⚠️ /admin/auth/verify DOIT être ici (miroir de /auth/verify côté client) : c'est une simple
      // VÉRIFICATION de session. Un 401 dessus (cache auth expiré, cold-start) ne doit PAS purger
      // l'adminToken ni rediriger vers login — sinon la restauration de session au boot déconnecte
      // le Super Admin à tort (cause du « déconnecté au retour accueil »).
      const publicAdminUrls = ['/admin/auth/login', '/admin/auth/signup', '/admin/auth/verify', '/admin/health'];

      const isPublicRoute = isAdminApi
        ? publicAdminUrls.some(u => url.includes(u))
        : publicClientUrls.some(u => url.includes(u));

      if (!isPublicRoute) {
        const errCode = error.response?.data?.code;
        // S1 — 4 motifs distincts (+ compat ancien SESSION_INVALIDATED). Le code → valeur ?session=
        // lue par les pages de login pour un message dédié.
        const SESSION_CODE_TO_PARAM = {
          SESSION_EXPIRED:     'expired',
          SESSION_INACTIVE:    'inactive',
          SESSION_REPLACED:    'replaced',
          SESSION_REVOKED:     'revoked',
          SESSION_INVALIDATED: 'revoked', // rétro-compat (ancien code)
        };
        const sessionParamValue = SESSION_CODE_TO_PARAM[errCode] || 'expired';
        // « Session définitivement morte » = remplacée/révoquée : on NE tente PAS de refresh silencieux
        // (une autre connexion a pris la place, ou révocation volontaire). expired/inactive peuvent, elles,
        // être renouvelées par le refresh token.
        const isSessionInvalidated = errCode === 'SESSION_INVALIDATED' || errCode === 'SESSION_REVOKED' || errCode === 'SESSION_REPLACED';
        const redirectParam = `session=${sessionParamValue}`;

        // 🌐 INVITÉ / ANONYME : aucune session client (ni `user`, ni `authToken` en localStorage).
        // Un 401 provient alors d'un jeton STATELESS invité (ex. checkout invité 3 h expiré) ou d'un
        // simple visiteur → il NE DOIT JAMAIS être redirigé vers /login ni déclencher /auth/refresh.
        // On laisse l'erreur remonter pour que la page invité l'affiche proprement et RESTE ouverte
        // (comme les meilleurs sites : un invité n'est jamais « déconnecté »). Note : « Retrouver
        // mon billet » stocke son jeton en sessionStorage → toujours traité comme invité ici.
        const hasClientSession = !!(localStorage.getItem('user') || localStorage.getItem('authToken'));
        const isAnonymousClient = !isAdminApi && !hasClientSession;

        // 🔄 CLIENT : avant de déconnecter, tenter UNE rotation via le refresh token (cookie).
        // Garde-fous anti-boucle : pas sur l'endpoint refresh lui-même, une seule tentative
        // par requête (_retried), session non explicitement révoquée, et JAMAIS pour un invité.
        const cfg = error.config || {};
        const isRefreshCall = url.includes('/auth/refresh');
        if (!isAdminApi && !isAnonymousClient && !isRefreshCall && !isSessionInvalidated && !cfg._retried) {
          try {
            cfg._retried = true;
            refreshPromise = refreshPromise || api.post('/auth/refresh', {}).finally(() => { refreshPromise = null; });
            await refreshPromise;
            return api(cfg); // rejoue la requête initiale avec le cookie authToken renouvelé
          } catch (_) { /* refresh échoué → on poursuit vers la déconnexion ci-dessous */ }
        }

        // 🔒 REPRISE DE CONTEXTE — mémorise la page EN COURS avant de rediriger vers le login.
        // Sans ça, une session expirée pendant une action (téléchargement de billet, paiement,
        // panier…) renvoyait à l'accueil après reconnexion au lieu de reprendre l'action.
        // Chemin relatif uniquement (anti open-redirect).
        const here = window.location.pathname + window.location.search;
        const safeHere = here.startsWith('/') && !here.startsWith('//') && !here.includes('://') ? here : null;

        if (isAdminApi) {
          logWarn(`[LOGOUT_REASON] ${isSessionInvalidated ? 'SESSION_REVOKED' : 'TOKEN_EXPIRED'} · app=admin · origine=${url}`);
          localStorage.removeItem("adminToken");
          localStorage.removeItem("admin");
          sessionStorage.removeItem("adminToken");
          sessionStorage.removeItem("admin");
          if (!window.location.pathname.includes('/admin/login')) {
            if (safeHere) sessionStorage.setItem("adminRedirectAfterLogin", safeHere);
            // 🔔 Point 2 — expiration de session pendant une action (ex. soumission d'un formulaire
            // bus après plusieurs heures) : AVANT la redirection, on affiche un message CLAIR sur la
            // page en cours (plus de « blocage silencieux »), puis on redirige après un court délai
            // pour laisser le temps de le lire. Le login rappelle aussi la raison (?session=expired).
            const SESSION_TOAST = {
              expired:  "Votre session a expiré (sécurité). Reconnectez-vous — votre action n'a pas été enregistrée.",
              inactive: "Session fermée pour inactivité. Reconnectez-vous pour continuer.",
              replaced: "Une autre connexion a pris la place de cette session. Reconnectez-vous pour continuer.",
              revoked:  "Votre session a été révoquée. Reconnectez-vous pour continuer.",
            };
            const expiredMsg = SESSION_TOAST[sessionParamValue] || SESSION_TOAST.expired;
            try { window.__toast?.({ type: 'warning', message: expiredMsg, duration: 6000 }); } catch (_) {}
            setTimeout(() => window.location.replace(`/admin/login?${redirectParam}`), 1100);
          }
        } else if (!isAnonymousClient) {
          logWarn(`[LOGOUT_REASON] ${isSessionInvalidated ? 'SESSION_REVOKED' : 'TOKEN_EXPIRED'} · app=client · origine=${url}`);
          localStorage.removeItem("authToken");
          localStorage.removeItem("user");
          sessionStorage.removeItem("authToken");
          sessionStorage.removeItem("user");
          if (!window.location.pathname.includes('/login')) {
            if (safeHere) sessionStorage.setItem("redirectAfterLogin", safeHere);
            window.location.replace(`/login?${redirectParam}`);
          }
        } else {
          // 🌐 INVITÉ : jeton stateless expiré / requête anonyme → AUCUNE redirection, AUCUN
          // nettoyage de session (il n'y en a pas). L'erreur remonte à la page qui l'affiche
          // proprement (« lien expiré → Retrouver mon billet ») ; la page reste ouverte.
          logWarn(`[GUEST_401] requête anonyme/jeton invité expiré · origine=${url} → page conservée`);
        }
      }
    }

    // Erreurs réseau / CORS (pas de response du tout) → message catalogue traduit + toast global.
    if (!error.response) {
      attachErrorContext(error);
      error.isNetwork = error.appCode === 'NETWORK_OFFLINE';
      // Toast global : les formulaires ne catchent pas toujours les erreurs réseau.
      // Pas de `duration` → l'erreur PERSISTE jusqu'à fermeture (lisibilité garantie, cf. Toast).
      window.__toast?.({ type: 'error', message: error.userMessage });
      // 📥 Incident : erreur réseau (throttlé côté client, dédupliqué côté serveur).
      reportFrontendError({ type: 'api_error', message: `Réseau: ${error.message}`, code: error.appCode, path: error.config?.url });
      return Promise.reject(error);
    }

    // Avec réponse : appCode + requestId + userMessage traduit (plus de message technique brut).
    attachErrorContext(error);

    // 📥 Incident : seules les 5xx sont remontées (les 4xx de validation = bruit, exclues).
    if (error.response.status >= 500) {
      reportFrontendError({
        type: 'api_error',
        http_status: error.response.status,
        code: error.response.data?.code,
        message: `API ${error.response.status} ${error.config?.method || ''} ${error.config?.url || ''}`.trim(),
      });
    }

    return Promise.reject(error);
  }
);

// 🪪 CONTEXTE DE CONSULTATION (super-admin « Voir en tant que compagnie »). Lu à la volée depuis
// localStorage (même source que l'intercepteur qui pose x-view-company-id). Entré dans la clé de
// cache ET contrôlé à l'arrivée des réponses → aucune donnée d'une compagnie ne fuite sur une autre.
const currentViewContext = () => {
  try {
    const v = localStorage.getItem("goweleViewCompanyId");
    return (v && v !== "null" && v !== "undefined") ? String(v) : "global";
  } catch (_) { return "global"; }
};

// Clé de cache = contexte compagnie + url + params. Exportée pour les tests (logique pure).
export const buildCacheKey = (ctx, url, params) => `${ctx}|${url}:${JSON.stringify(params || {})}`;
export { currentViewContext as __currentViewContext };

// Service principal (inchangé sauf logs)
export const secureApiService = {
  get: (url, config = {}, options = {}) => {
    // Lot 1 — la clé inclut le CONTEXTE compagnie : passer de A à B produit une clé différente,
    // donc jamais de cache-hit croisé entre deux compagnies (ni avec la vue globale).
    const ctxAtStart = currentViewContext();
    const cacheKey = buildCacheKey(ctxAtStart, url, config.params);

    if (options.useCache !== false) {
      const cached = smartCache.get(cacheKey);
      if (cached) {
        log("📦 Cache Hit:", url);
        return Promise.resolve(cached);
      }
    }

    return api.get(url, config).then(response => {
      // 🛡️ RÉPONSE TARDIVE : si le contexte compagnie a CHANGÉ entre le départ de la requête et son
      // retour (l'utilisateur a switché A→B pendant le chargement), ce résultat appartient à A et ne
      // doit NI être mis en cache NI atteindre l'écran (qui affiche désormais B). On ABANDONNE
      // SILENCIEUSEMENT via une promesse jamais résolue : le changement de contexte a déjà déclenché
      // un re-fetch (B), dont la réponse mettra à jour l'écran. Aucune erreur affichée, aucune fuite.
      if (currentViewContext() !== ctxAtStart) {
        log(`🚫 Réponse ignorée (contexte changé ${ctxAtStart}→${currentViewContext()}): ${url}`);
        return new Promise(() => {}); // suspendue → n'atteint ni .then ni .catch de l'appelant
      }
      if (options.useCache !== false) {
        const timeout = options.cacheTimeout || 30000;
        smartCache.set(cacheKey, response, timeout);
        log(`💾 Cache Set: ${url} (${timeout}ms)`);
      }
      return response;
    });
  },

  post: (url, data, config = {}) => {
    secureApiService.invalidateRelatedCache(url);
    log(`📤 POST ${url}`);
    return api.post(url, data, config);
  },

  put: (url, data, config = {}) => {
    secureApiService.invalidateRelatedCache(url);
    log(`✏️ PUT ${url}`);
    return api.put(url, data, config);
  },

  patch: (url, data, config = {}) => {
    secureApiService.invalidateRelatedCache(url);
    log(`🔧 PATCH ${url}`);
    return api.patch(url, data, config);
  },

  delete: (url, config = {}) => {
    secureApiService.invalidateRelatedCache(url);
    log(`🗑️ DELETE ${url}`);
    return api.delete(url, config);
  },

  invalidateRelatedCache: (url) => {
    log(`🔄 Invalidation cache pour: ${url}`);
    
    if (url.includes('/reservations')) {
      smartCache.clearByPattern('/reservations');
      smartCache.clearByPattern('/trajets');
    }
    if (url.includes('/trajets')) {
      smartCache.clearByPattern('/trajets');
    }
    // Inclut les actions super-admin (promote-user, /super/*) et chauffeurs : elles
    // modifient la liste des utilisateurs mais l'URL ne contient pas "/users".
    if (url.includes('/users') || url.includes('/profile') || url.includes('promote') ||
        url.includes('/super') || url.includes('/chauffeurs') || url.includes('password')) {
      smartCache.clearByPattern('/users');
      smartCache.clearByPattern('/profile');
      smartCache.clearByPattern('/super');
      smartCache.clearByPattern('/chauffeurs');
    }
    if (url.includes('/societes') || url.includes('/super')) {
      smartCache.clearByPattern('/societes');
    }
    if (url.includes('/auth')) {
      smartCache.clearByPattern('/users');
      smartCache.clearByPattern('/profile');
      smartCache.clearByPattern('/reservations');
    }
    // 🔧 Corbeille : restaurer/purger touche les listes de TOUTES les entités concernées
    // (societes, trajets, bus, chauffeurs, reservations, agences) mais l'URL ne contient
    // que "/trash" → sans ça, ces listes restaient en cache périmé après une action corbeille
    // (obligation de rafraîchir la page). On invalide donc largement + la corbeille elle-même.
    if (url.includes('/trash')) {
      ['/trash', '/societes', '/trajets', '/bus', '/chauffeurs', '/reservations', '/agences', '/arrets']
        .forEach((p) => smartCache.clearByPattern(p));
    }
  },

  upload: (url, formData, config = {}) => {
    secureApiService.invalidateRelatedCache(url);
    return api.post(url, formData, {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...config.headers
      }
    });
  },

  // 🪪 Lot 1 — Purge TOTALE du cache. Appelée au changement de « Voir en tant que compagnie » :
  // toutes les entités changent de périmètre d'un coup → on repart d'un cache vierge (le nouveau
  // contexte entre déjà dans la clé, mais on purge pour libérer la mémoire et éviter tout résidu).
  clearAllCache: () => { smartCache.clear(); log('🧹 Cache entièrement purgé (changement de contexte compagnie)'); },
};

// petits utilitaires (inchangés)
export const checkAPIHealth = () => 
  api.get('/health', {}, { useCache: false })
    .then(() => ({ 
      status: 'online', 
      timestamp: new Date().toISOString(),
      message: 'API opérationnelle'
    }))
    .catch((error) => ({ 
      status: 'offline', 
      timestamp: new Date().toISOString(),
      message: error.userMessage || 'API indisponible',
      error: error.message
    }));

export const apiMonitor = {
  getCacheStats: () => ({ 
    size: smartCache.getSize(),
    timestamp: new Date().toISOString()
  }),
  
  getCacheEntries: () => {
    const entries = [];
    for (const [key, value] of smartCache.cache.entries()) {
      entries.push({
        key,
        expiresIn: value.expiresAt - Date.now(),
        url: key.split(':')[0]
      });
    }
    return entries;
  },
  
  getPerformance: () => ({
    uptime: typeof process !== 'undefined' && process.uptime ? process.uptime() : 'N/A',
    timestamp: new Date().toISOString()
  })
};

export const cacheManager = {
  clearAll: () => {
    const size = smartCache.getSize();
    smartCache.clear();
    log(`🧹 Cache vidé - ${size} entrées supprimées`);
    return size;
  },
  
  clearByPattern: (pattern) => {
    const beforeSize = smartCache.getSize();
    smartCache.clearByPattern(pattern);
    const afterSize = smartCache.getSize();
    const cleared = beforeSize - afterSize;
    log(`🧹 Cache partiellement vidé - ${cleared} entrées supprimées pour: ${pattern}`);
    return cleared;
  },
  
  clearExpired: () => {
    const now = Date.now();
    let cleaned = 0;
    
    for (const [key, value] of smartCache.cache.entries()) {
      if (now > value.expiresAt) {
        smartCache.cache.delete(key);
        cleaned++;
      }
    }
    
    log(`🧹 Cache: ${cleaned} éléments expirés supprimés`);
    return cleaned;
  },
  
  getInfo: () => ({
    totalEntries: smartCache.getSize(),
    timestamp: new Date().toISOString()
  })
};

setInterval(() => {
  cacheManager.clearExpired();
}, 300000);

// Keep-alive: ping le backend toutes les 10 min pour éviter le cold start Render (free tier)
// Le ping est silencieux - n'affecte pas l'UX et empêche la dormance après 15 min d'inactivité
if (process.env.NODE_ENV === 'production') {
  setInterval(() => {
    fetch(`${API_CONFIG.baseURL}/health`, { method: 'GET', cache: 'no-store' }).catch(() => {});
  }, 10 * 60 * 1000); // toutes les 10 minutes
}

// 🔄 INVALIDATION DE CACHE PILOTÉE PAR LE TEMPS RÉEL (PROMPT 6).
// Une mutation faite par un AUTRE utilisateur arrive via socket → window 'admin:data_changed'.
// On purge ICI le cache admin de l'entité concernée AVANT que useRealtimeRefresh ne relance
// le fetch (throttlé à 450 ms) → le refetch repart de l'API, jamais du cache périmé (TTL 30s).
// Garantit la cohérence Front ↔ API ↔ Base sans F5.
if (typeof window !== 'undefined') {
  const ENTITY_CACHE_PATTERNS = {
    reservation:  ['/admin/reservations', '/admin/dashboard', '/admin/reports', '/admin/finance'],
    trajet:       ['/admin/trajets', '/admin/dashboard'],
    bus:          ['/admin/bus', '/admin/dashboard'],
    chauffeur:    ['/admin/chauffeurs', '/admin/dashboard'],
    agence:       ['/admin/agences'],
    avis:         ['/admin/avis'],
    paiement:     ['/admin/paiements', '/admin/finance', '/admin/reports', '/admin/dashboard'],
    user:         ['/admin/users', '/admin/team'],
    societe:      ['/admin/societes', '/admin/dashboard'],
    message:      ['/admin/messages'],
    subscription: ['/admin/subscriptions'],
    notification: ['/admin/notifications'],
    arret:        ['/admin/arrets'],
  };
  window.addEventListener('admin:data_changed', (e) => {
    const entity = e?.detail?.entity;
    // Entité connue → purge ciblée ; inconnue → purge large de tout le cache admin.
    const patterns = (entity && ENTITY_CACHE_PATTERNS[entity]) || ['/admin/'];
    patterns.forEach(p => smartCache.clearByPattern(p));
  });
}

export const resetApiService = () => {
  smartCache.clear();
  log('🔄 API Service réinitialisé - Cache vidé');
};

export default api;
